import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Calendar,
  Loader2,
  Mail,
  Phone,
  User as UserIcon,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  Shield,
} from "lucide-react";
import { usersService } from "../../../services/usersService";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { Layout } from "../../../layouts/DashboardLayout";

export interface NewUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phonenumber: string;
  password: string;
  confirm_password?: string;
  country_id: number;
  role: "USER" | "ADMIN" | "MODERATOR";
  birthdate: string;
  send_credentials_email: boolean;
}

const initialUserForm: NewUserFormData = {
  first_name: "",
  last_name: "",
  email: "",
  phonenumber: "",
  password: "",
  confirm_password: "",
  country_id: 1,
  role: "USER",
  birthdate: "",
  send_credentials_email: true,
};

export const NewUserPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<NewUserFormData>(initialUserForm);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "country_id" ? Number(value) : value,
    }));
  };

  const validateUserForm = (): boolean => {
    if (!formData.first_name.trim()) {
      alertError("Le prénom est requis");
      return false;
    }
    if (!formData.last_name.trim()) {
      alertError("Le nom est requis");
      return false;
    }
    if (!formData.email.trim()) {
      alertError("L'email est requis");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alertError("L'email est invalide");
      return false;
    }
    if (!formData.password) {
      alertError("Le mot de passe est requis");
      return false;
    }
    if (formData.password.length < 6) {
      alertError("Le mot de passe doit contenir au moins 6 caractères");
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      alertError("Les mots de passe ne correspondent pas");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateUserForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alertError("Votre session est expirée. Veuillez vous reconnecter");
        return;
      }

      const payload = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phonenumber: formData.phonenumber.trim(),
        country_id: formData.country_id,
        password: formData.password,
        role: formData.role,
        birthdate: formData.birthdate || "",
        send_credentials_email: formData.send_credentials_email,
      };

      const response = await usersService.createUserByAdmin(payload, token);

      if (!response?.data && !response?.id) {
        alertError(response?.message || "Erreur lors de la création de l'utilisateur");
        return;
      }

      alertSuccess("Utilisateur créé avec succès");
      navigate("/izilife-pay/users");
    } catch (error: any) {
      console.error("Erreur lors de la création :", error);
      alertError(error?.response?.data?.message || "Erreur lors de la création de l'utilisateur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* En-tête */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Créer un nouvel utilisateur</h1>
            </div>
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600" disabled={loading}>
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Carte du formulaire */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations personnelles */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Informations personnelles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4" /> Prénom <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4" /> Nom <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Contact</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Téléphone
                      </div>
                    </label>
                    <input
                      type="tel"
                      name="phonenumber"
                      value={formData.phonenumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Sécurité */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Sécurité</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" /> Mot de passe <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" /> Confirmer le mot de passe <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates & Rôle */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Informations complémentaires</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Date de naissance
                      </div>
                    </label>
                    <input
                      type="date"
                      name="birthdate"
                      value={formData.birthdate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" /> Rôle
                      </div>
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="USER">Utilisateur</option>
                      <option value="ADMIN">Administrateur</option>
                      <option value="MODERATOR">Modérateur</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Localisation */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Localisation</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Pays</label>
                    <select
                      name="country_id"
                      value={formData.country_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value={1}>Canada 🇨🇦</option>
                      <option value={2}>Cameroun 🇨🇲</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Options</h2>
                <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="send_credentials_email"
                    checked={formData.send_credentials_email}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Envoyer les identifiants par email</p>
                    <p className="text-xs text-gray-500">
                      L'utilisateur recevra un email avec ses identifiants de connexion
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Créer l'utilisateur
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewUserPage;
